// src/features/providers/api.ts
import {
  FlagNotFoundError,
  TypeMismatchError,
  TargetingKeyMissingError,
  ProviderStatus,
  ProviderEvents,
  OpenFeatureEventEmitter
} from "@openfeature/web-sdk";
var FetchError = class extends Error {
  status;
  constructor(status) {
    super(`Request failed with status code ${status}`);
    this.status = status;
  }
};
function transformContext(context) {
  const { targetingKey, ...attributes } = context;
  if (targetingKey === void 0 || targetingKey === null || targetingKey === "") {
    throw new TargetingKeyMissingError();
  }
  return {
    targetingKey,
    ...attributes
  };
}
var StaffingEngineFeatureProvider = class _StaffingEngineFeatureProvider {
  metadata = {
    name: "Staffing Engine Provider"
  };
  // emitter for provider events
  events = new OpenFeatureEventEmitter();
  // logger is the Open Feature logger to use
  _logger;
  _endpoint;
  _apiTimeout;
  _apiKey;
  // initial delay in millisecond to wait before retrying to connect
  _retryInitialDelay;
  // multiplier of _retryInitialDelay after each failure
  _retryDelayMultiplier;
  // maximum number of retries
  _maxRetries;
  // status of the provider
  _status = ProviderStatus.NOT_READY;
  // _flags is the in memory representation of all the flags.
  _flags = {};
  constructor(options, logger) {
    this._logger = logger;
    this._apiTimeout = options.apiTimeout || 0;
    this._endpoint = options.endpoint;
    this._retryInitialDelay = options.retryInitialDelay || 100;
    this._retryDelayMultiplier = options.retryDelayMultiplier || 2;
    this._maxRetries = options.maxRetries || 5;
    this._apiKey = options.apiKey;
  }
  get status() {
    return this._status;
  }
  async initialize(context) {
    try {
      await this.fetchAll(context);
      this._status = ProviderStatus.READY;
      this._logger?.debug(
        `${_StaffingEngineFeatureProvider.name}: staffing engine feature flags provider initialized`
      );
    } catch (error) {
      this._logger?.error(
        `${_StaffingEngineFeatureProvider.name}: initialization failed, provider is on error, we will try to reconnect: ${error}`
      );
      this._status = ProviderStatus.ERROR;
      this.handleFetchErrors(error);
      this.retryFetchAll(context);
    }
  }
  async retryFetchAll(ctx) {
    let delay = this._retryInitialDelay;
    let attempt = 0;
    while (attempt < this._maxRetries) {
      attempt++;
      try {
        await this.fetchAll(ctx);
        this._status = ProviderStatus.READY;
        return;
      } catch (err) {
        this._status = ProviderStatus.ERROR;
        this.handleFetchErrors(err);
        await new Promise((resolve) => setTimeout(resolve, delay));
        delay *= this._retryDelayMultiplier;
        this._logger?.info(
          `${_StaffingEngineFeatureProvider.name}: Waiting ${delay} ms before trying to evaluate the flags (${attempt}/${this._maxRetries}).`
        );
      }
    }
  }
  /**
   * fetchAll is a function that is calling Staffing Engine Feature Flag to bulk evaluate flags.
   * It emits an event to notify when it is ready or on error.
   *
   * @param context - The static evaluation context
   * @private
   */
  async fetchAll(context) {
    const endpointURL = new URL(this._endpoint);
    const path = "feature_flags/flags";
    endpointURL.pathname = endpointURL.pathname.endsWith("/") ? endpointURL.pathname + path : endpointURL.pathname + "/" + path;
    const request = { evaluationContext: transformContext(context) };
    const headers = new Headers({
      "Content-Type": "application/json",
      Accept: "application/json"
    });
    if (this._apiKey) {
      headers.set("Authorization", `Bearer ${this._apiKey}`);
    }
    const init2 = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify(request)
    };
    const response = await fetch(endpointURL.toString(), init2);
    if (!response?.ok) {
      throw new FetchError(response.status);
    }
    const data = await response.json();
    let flags = {};
    Object.keys(data.flags).forEach((currentValue) => {
      const resolved = data.flags[currentValue];
      const resolutionDetails = {
        value: resolved.value,
        variant: resolved.variant,
        errorCode: resolved.errorCode,
        flagMetadata: resolved.metadata,
        reason: resolved.reason
      };
      flags = {
        ...flags,
        [currentValue]: resolutionDetails
      };
    });
    this._flags = flags;
    if (this._flags) {
      this.events.emit(ProviderEvents.ConfigurationChanged, {
        message: "flag configuration has changed",
        flagsChanged: Object.keys(flags)
      });
    }
  }
  /**
   * handleFetchErrors is a function that take care of the errors that can be thrown
   * inside the FetchAll method.
   *
   * @param error - The error thrown
   * @private
   */
  handleFetchErrors(error) {
    if (error instanceof FetchError) {
      this.events.emit(ProviderEvents.Error, {
        message: error.message
      });
      if (error.status == 401) {
        this._logger?.error(
          `${_StaffingEngineFeatureProvider.name}: invalid token used to contact Staffing Engine Feature Flag instance: ${error}`
        );
      } else if (error.status === 404) {
        this._logger?.error(
          `${_StaffingEngineFeatureProvider.name}: impossible to call Staffing Engine Feature Flag instace: ${error}`
        );
      } else {
        this._logger?.error(
          `${_StaffingEngineFeatureProvider.name}: unknown error while retrieving flags: ${error}`
        );
      }
    } else {
      this._logger?.error(
        `${_StaffingEngineFeatureProvider.name}: unknown error while retrieving flags: ${error}`
      );
      this.events.emit(ProviderEvents.Error, {
        message: "unknown error while retrieving flags"
      });
    }
  }
  async onContextChange(_, newContext) {
    this._logger?.debug(
      `${_StaffingEngineFeatureProvider.name}: new context provided: ${newContext}`
    );
    this.events.emit(ProviderEvents.Stale, { message: "context has changed", context: newContext });
    await this.retryFetchAll(newContext);
    this.events.emit(ProviderEvents.Ready, { message: "provider is ready" });
  }
  // flagKey: string, defaultValue: number, context: EvaluationContext, logger: Logger
  resolveNumberEvaluation(flagKey) {
    return this.evaluate(flagKey, "number");
  }
  resolveObjectEvaluation(flagKey) {
    return this.evaluate(flagKey, "object");
  }
  resolveStringEvaluation(flagKey) {
    return this.evaluate(flagKey, "string");
  }
  resolveBooleanEvaluation(flagKey) {
    return this.evaluate(flagKey, "boolean");
  }
  evaluate(flagKey, type) {
    const resolved = this._flags[flagKey];
    if (!resolved) {
      throw new FlagNotFoundError(`flag key ${flagKey} not found in cache`);
    }
    if (typeof resolved.value !== type) {
      throw new TypeMismatchError(`flag key ${flagKey} is not of type ${type}`);
    }
    return {
      variant: resolved.variant,
      value: resolved.value,
      flagMetadata: resolved.flagMetadata,
      errorCode: resolved.errorCode,
      errorMessage: resolved.errorMessage,
      reason: resolved.reason
    };
  }
};

// src/features/providers/env.ts
import {
  FlagNotFoundError as FlagNotFoundError2,
  GeneralError,
  OpenFeatureEventEmitter as OpenFeatureEventEmitter2,
  ProviderEvents as ProviderEvents2,
  StandardResolutionReasons,
  TypeMismatchError as TypeMismatchError2
} from "@openfeature/web-sdk";
var EnvProvider = class {
  events = new OpenFeatureEventEmitter2();
  metadata = {
    name: "Env Provider"
  };
  _flagConfiguration;
  constructor(flagConfiguration = {}) {
    this._flagConfiguration = { ...flagConfiguration };
  }
  replaceConfiguration(flagConfiguration) {
    const flagsChanged = Object.entries(flagConfiguration).filter(([key, value]) => this._flagConfiguration[key] !== value).map(([key]) => key);
    this._flagConfiguration = { ...flagConfiguration };
    this.events.emit(ProviderEvents2.ConfigurationChanged, { flagsChanged });
  }
  resolveBooleanEvaluation(flagKey) {
    const flagValue = this.lookupFlagValueOrThrow(flagKey);
    if (typeof flagValue !== "boolean") {
      throw new TypeMismatchError2();
    }
    return staticResolution(flagValue);
  }
  resolveStringEvaluation(flagKey) {
    const flagValue = this.lookupFlagValueOrThrow(flagKey);
    if (typeof flagValue !== "string") {
      throw new TypeMismatchError2();
    }
    return staticResolution(flagValue);
  }
  resolveNumberEvaluation(flagKey) {
    const flagValue = this.lookupFlagValueOrThrow(flagKey);
    if (typeof flagValue !== "number") {
      throw new TypeMismatchError2();
    }
    return staticResolution(flagValue);
  }
  resolveObjectEvaluation(flagKey) {
    throw new GeneralError("support for object flags has not been implemented");
  }
  lookupFlagValueOrThrow(flagKey) {
    if (!(flagKey in this._flagConfiguration)) {
      throw new FlagNotFoundError2();
    }
    return this._flagConfiguration[flagKey];
  }
};
function staticResolution(value) {
  return {
    value,
    reason: StandardResolutionReasons.STATIC
  };
}

// src/features/config.ts
var FEATURE_FLAGS_KNOWN = {
  ["product_engine_gpt" /* ProductEngineGpt */]: "boolean",
  ["product_chatbot_alerts" /* ProductChatbotAlerts */]: "boolean",
  ["feature_supabase_data" /* FeatureSupabaseData */]: "boolean",
  ["feature_organizations_v2" /* FeatureOrganizationsV2 */]: "boolean",
  ["feature_show_email_auth" /* FeatureShowEmailAuth */]: "boolean"
};
var LOCAL_OVERRIDES = {};

// src/sentry/index.ts
import * as Sentry3 from "@sentry/vue";

// src/sentry/plugin.ts
import * as Sentry from "@sentry/vue";
import "vue";
import "vue-router";
var SENTRY_TRACE_SAMPLE_RATE = 1;
var SENTRY_REPLAY_SESSION_RATE = 0.1;
var SENTRY_REPLAY_ERROR_RATE = 1;
var SentryPlugin = {
  install(app, router, options) {
    Sentry.init({
      app,
      enabled: options.enabled,
      debug: options.debug,
      dsn: options.dsn,
      environment: options.environment,
      release: options.release,
      // Misc
      ignoreErrors: options.ignoreErrors,
      // Integrations
      // See: https://github.com/getsentry/sentry-javascript/issues/12706
      /* eslint-disable import/namespace */
      integrations: [
        Sentry.browserTracingIntegration({
          router,
          routeLabel: "path"
        }),
        Sentry.replayIntegration(),
        Sentry.extraErrorDataIntegration({
          depth: 4,
          captureErrorCause: true
        }),
        Sentry.breadcrumbsIntegration({
          // Prevent capturing console breadcrumbs
          console: false
        }),
        Sentry.httpClientIntegration()
      ],
      /* eslint-enable import/namespace */
      // Browser tracing
      tracesSampleRate: SENTRY_TRACE_SAMPLE_RATE || 1,
      tracePropagationTargets: getTracingTargets(options.tracePropagationTargets),
      tracingOptions: {
        trackComponents: true,
        hooks: ["activate", "mount", "create", "update", "unmount"]
      },
      // Replay
      replaysSessionSampleRate: SENTRY_REPLAY_SESSION_RATE || 0.1,
      replaysOnErrorSampleRate: SENTRY_REPLAY_ERROR_RATE || 1,
      // Vue
      attachProps: true,
      logErrors: true
    });
    app.config.globalProperties.$sentry = Sentry;
  }
};
var getTracingTargets = (targets) => {
  const defaultTargets = [
    "localhost",
    /^https:\/\/supabase\.staffingengine\.ai/,
    /^https:\/\/staffingengine-staging\.supabase\.co/
  ];
  if (!targets) return defaultTargets;
  return [...defaultTargets, ...targets];
};

// src/sentry/composable.ts
import * as Sentry2 from "@sentry/vue";
var useSentry = () => Sentry2;

// src/mixpanel/index.ts
import "mixpanel-browser";

// src/mixpanel/plugin.ts
import { default as mixpanel } from "mixpanel-browser";
import "vue";
var MixpanelPlugin = {
  install(app, options) {
    if (!options.token) throw new Error("[Mixpanel Plugin] `token` is required!");
    initMixpanel(options);
    if (options.enabled === false) {
      if (options.debug) {
        console.warn("[Mixpanel Plugin] mixpanel is disabled");
      }
      mixpanel.disable();
    }
    app.config.globalProperties.$mixpanel = mixpanel;
  }
};
var initMixpanel = (options) => {
  const IS_DEV = options?.devMode === true;
  const config = {
    debug: options?.debug || false,
    track_pageview: options?.track_pageview || "url-with-path",
    batch_requests: true,
    persistence: "localStorage",
    ignore_dnt: IS_DEV,
    // "Secret?" error handler :)
    error_reporter: (msg, err) => handleMixpanelError(msg, err, IS_DEV)
  };
  try {
    mixpanel.init(options.token, config);
  } catch (err) {
    if (IS_DEV) {
      const error = err;
      console.warn(`[Mixpanel Plugin] Unable to start mixpanel: ${error.message}`, error);
    }
  }
};
var handleMixpanelError = (msg, err, shouldLog = false) => {
  if (shouldLog) {
    console.warn("[Mixpanel Plugin] error", msg, err);
  }
  if (msg.message.includes("Bad HTTP status")) {
    if (shouldLog) {
      console.warn(
        "[Mixpanel Plugin] logging disabled due to mixpanel being blocked by browser plugins/settings"
      );
    }
    mixpanel.disable();
  }
};

// src/mixpanel/composable.ts
import { default as mixpanel2 } from "mixpanel-browser";
var useMixpanel = () => mixpanel2;

// src/composition/collection.ts
import { isProxy, ref, toRaw, unref } from "vue";

// src/utils/arrays.ts
var merge = (a, b, property) => a.filter(
  (aa) => !b.find((bb) => aa[property] === bb[property])
).concat(b);
var deepEqual = (x, y) => {
  const ok = Object.keys, tx = typeof x, ty = typeof y;
  return x && y && tx === "object" && tx === ty ? ok(x).length === ok(y).length && ok(x).every((key) => deepEqual(x[key], y[key])) : x === y;
};
var getIndexOfObjectInArray = (target, property, value) => {
  const index = target.findIndex((item) => item[property] === value);
  if (index === -1) return void 0;
  return index;
};
var updateByIndex = (target, index, toUpdate) => {
  target.splice(index, 1, toUpdate);
};
var removeByIndex = (target, index) => {
  target.splice(index, 1);
};

// src/composition/collection.ts
var useCollection = (initialValue = []) => {
  const collection = ref(initialValue);
  const add = (val) => {
    collection.value.push(val);
  };
  const updateBy = (key, val, {
    add: add2
  } = {
    add: true
  }) => {
    const maybeAdd = () => {
      if (!add2) return;
      collection.value.push(val);
    };
    if (!collection.value.length) {
      maybeAdd();
      return;
    }
    const existingIndex = getIndexOfObjectInArray(asRaw(collection), key, val[key]);
    if (existingIndex === void 0) {
      maybeAdd();
      return;
    }
    collection.value.splice(existingIndex, 1, val);
  };
  const removeBy = (key, val) => {
    if (!collection.value.length) return;
    const index = getIndexOfObjectInArray(collection.value, key, val[key]);
    if (index === void 0) return;
    collection.value.splice(index, 1);
  };
  const mergeBy = (key, values) => {
    if (!values.length) return;
    if (!collection.value.length) {
      collection.value.push(...values);
      return;
    }
    for (const newItem of values) {
      const index = getIndexOfObjectInArray(collection.value, key, newItem[key]);
      if (index === void 0) {
        collection.value.push(newItem);
      } else {
        collection.value.splice(index, 1, newItem);
      }
    }
  };
  const empty = () => {
    collection.value = [];
  };
  const manageCollection = {
    add,
    updateBy,
    removeBy,
    mergeBy,
    empty
  };
  return [collection, manageCollection];
};
var asRaw = (val) => {
  if (isProxy(val)) return toRaw(unref(val));
  return unref(val);
};

// src/composition/concurrency.ts
async function useConcurrency(promises) {
  const results = await Promise.allSettled(promises);
  const rejections = results.map((result, index) => ({ ...result, index })).filter((mappedResults) => mappedResults.status === "rejected");
  const hasError = rejections.length > 0;
  const errors = rejections.reduce(
    (o, rejected) => ({ ...o, [rejected.index]: rejected.reason }),
    {}
  );
  return {
    results,
    hasError,
    errors
  };
}

// src/utils/dates.ts
function isValidDate(date) {
  try {
    const _date = date instanceof Date ? date : new Date(date);
    return !isNaN(_date.valueOf());
  } catch (error) {
    return false;
  }
}
var toUnixMs = (date) => date.getTime() / 1e3;

// src/utils/objects.ts
var removeUndefinedKeysFromObject = (obj) => Object.keys(obj).reduce((acc, key) => {
  if (obj[key] !== void 0) {
    acc[key] = obj[key];
  }
  return acc;
}, {});
function isKeyOfObject(key, obj) {
  return key in obj;
}
var isEmptyObject = (val) => {
  return Object.keys(val).length === 0;
};
function getNestedValue(obj, path, defaultValue = void 0) {
  const keys = Array.isArray(path) ? path : path.split(".");
  return keys.reduce((value, key) => {
    if (isKeyOfObject(key, value)) {
      return value[key];
    }
    return defaultValue;
  }, obj);
}
function setNestedValue(obj, path, value) {
  const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g);
  pathArray?.reduce((acc, key, i) => {
    if (acc[key] === void 0) acc[key] = {};
    if (i === pathArray.length - 1) acc[key] = value;
    return acc[key];
  }, obj);
}
var parseFormData = (data) => {
  const parsedData = {};
  const safeParse = (value) => {
    try {
      return JSON.parse(value);
    } catch (e) {
      return value;
    }
  };
  for (const [key, value] of data.entries()) {
    setNestedValue(parsedData, key, safeParse(value));
  }
  return parsedData;
};

// src/utils/templating.ts
var renderDisplayTemplate = (template, params, {
  fallback
} = {
  fallback: "(unknown)"
}) => {
  const replacer = (match) => {
    const paramKey = match.substring(2, match.length - 2);
    return params[paramKey] || fallback;
  };
  return template.replace(/\[\[\w+\]\]/g, replacer);
};

// src/utils/text.ts
var toKebabCase = (input) => input.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
var toCamelCase = (input) => {
  return input.replace(/\b[A-Z]/, (match) => match.toLowerCase()).replace(/(_\w)/g, (match) => match[1].toUpperCase()).replace(/-./g, (match) => match[1].toUpperCase());
};
var camelizeKeys = (obj) => {
  if (!obj) {
    throw new Error("camelizeKeys takes an object as a parameter");
  }
  return Object.fromEntries(Object.entries(obj).map(([key, val]) => [toCamelCase(key), val]));
};
var capitalize = ([first, ...remainder]) => first.toUpperCase() + remainder.join("");
var UUID_NIL = "00000000-0000-0000-0000-000000000000";
var UUID_MAX = "ffffffff-ffff-ffff-ffff-ffffffffffff";
var isUUID = (val, options = {}) => {
  if (val === void 0 || val === null) return false;
  const _options = {
    nil: true,
    max: true,
    ...options
  };
  if (_options.nil && val === UUID_NIL) return true;
  if (_options.max && val === UUID_MAX) return true;
  return !!val.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i);
};

// src/utils/url.ts
var getUrlParam = (url, param) => {
  const regex = new RegExp(`[?&#]${param.replace(/[\[\]]/g, "\\$&")}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return "";
  return decodeURIComponent(results[2].replace(/\+/g, " "));
};
var serializeQueryParams = (obj, nest = "") => {
  const stringify = (item) => {
    if (item === void 0) return "undefined";
    if (item === null) return "null";
    return encodeURIComponent(item);
  };
  const encode = (key, val) => [key, stringify(val)].join("=");
  const nesting = nest ? `${nest}.` : "";
  const mappedObject = Object.entries(obj).map(([key, val]) => {
    if (Array.isArray(val))
      return val.map((nestedVal) => encode(`${nesting}${key}[]`, nestedVal)).join("&");
    else if (typeof val === "object" && val) return serializeQueryParams(val, `${nesting}${key}`);
    else return encode(`${nesting}${key}`, val);
  });
  return mappedObject.join("&");
};
var deserializeQueryParams = (queryParams, unnest = "") => {
  const params = new URLSearchParams(queryParams);
  const unstringify = (item) => {
    const decodedItem = decodeURIComponent(item);
    if (decodedItem === "undefined") return void 0;
    if (decodedItem === "null") return null;
    return decodedItem;
  };
  const result = {};
  for (const [key, val] of params.entries()) {
    const path = unnest ? key.replace(`${unnest}.`, "") : key;
    const value = unstringify(val);
    if (path.endsWith("[]")) {
      const k = path.replace("[]", "");
      const currentVal = getNestedValue(result, k);
      const newVal = currentVal ? [...currentVal, value] : [value];
      setNestedValue(result, k, newVal);
    } else {
      setNestedValue(result, path, value);
    }
  }
  return result;
};

// src/utils/elements.ts
var setProperty = (id, property, value, {
  throwOnError,
  noLog
} = {
  throwOnError: false,
  noLog: false
}) => {
  const component = document.getElementById(id);
  if (!component) {
    const message = `Cannot set "${property.toString()}" on element "#${id}": element not found`;
    if (throwOnError) throw new Error(message);
    if (!noLog) console.warn(message);
    return;
  }
  try {
    component[property] = value;
  } catch (error) {
    const message = `Cannot set "${property.toString()}" on element "#${id}": property not found`;
    if (throwOnError) throw new Error(message);
    if (!noLog) console.warn(message);
  }
};
var registerEvent = (id, event, callback, {
  throwOnError,
  noLog
} = {
  throwOnError: false,
  noLog: false
}) => {
  const component = document.getElementById(id);
  if (!component) {
    const message = `Cannot add listener for "${event}" on element "#${id}": element not found`;
    if (throwOnError) throw new Error(message);
    if (!noLog) console.warn(message);
    return;
  }
  component.addEventListener(event, callback);
};
export {
  EnvProvider,
  FEATURE_FLAGS_KNOWN,
  FetchError,
  LOCAL_OVERRIDES,
  MixpanelPlugin,
  Sentry3 as SentryLib,
  SentryPlugin,
  StaffingEngineFeatureProvider,
  camelizeKeys,
  capitalize,
  deepEqual,
  deserializeQueryParams,
  getIndexOfObjectInArray,
  getNestedValue,
  getUrlParam,
  isEmptyObject,
  isKeyOfObject,
  isUUID,
  isValidDate,
  merge,
  parseFormData,
  registerEvent,
  removeByIndex,
  removeUndefinedKeysFromObject,
  renderDisplayTemplate,
  serializeQueryParams,
  setNestedValue,
  setProperty,
  toCamelCase,
  toKebabCase,
  toUnixMs,
  updateByIndex,
  useCollection,
  useConcurrency,
  useMixpanel,
  useSentry
};
//# sourceMappingURL=index.js.map